import { Card, Typography } from '../ui';
import { AgentConnectionsList, AiCodersConnect, GeminiConnect } from '../settings-connections';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useProductName } from '../../../lib/use-brand';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { connectorsTargetElementId, type ChannelSlug } from '../../../lib/connectors-route';
import { AI_CODERS_ITEM_TITLE_KEYS, filterChannelSections } from './channel-search';
import { WhatsappChannelSection } from './WhatsappChannelSection';
import { MeetingsChannelSection } from './MeetingsChannelSection';
import { useMeetingsAgentEnabled } from '../../../lib/meetings-agent-flag';
import styles from './ChannelsBody.module.css';

const CHANNELS_ROUTE = 'settings-connectors';

// Literal store endpoints for the Phone card. Never localized.
const APP_STORE_URL = 'https://apps.apple.com/app/id6737450738';
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.wix.time';

/**
 * The Channels half (Figma 14013-148717): the surfaces Symphony can reach you through, grouped as the
 * redesign draws them — **WhatsApp** (its hub), **AI coders** (the coder connect cards WITH Gemini
 * Enterprise folded in), and **Web & Apps** (Symphony's own surfaces — web, phone, CarPlay).
 *
 * The four route slugs (`CHANNEL_SLUGS`) are the untouched deeplink contract the natives mirror; Gemini
 * no longer renders as its own section, so its scroll anchor lives inside the AI-coders section instead.
 * The search field lives in the page's control row and is passed in as `query`.
 */

/** A "Web & Apps" surface card (Figma 14013-148755): a title + availability, and — when the app ships
 *  there — the official store badges on the right, each linking to that store. */
interface StoreLink {
  /** The store listing this badge links to. */
  href: string;
  /** The official badge asset in /public (App Store SVG / Google Play PNG — the vendors' own artwork). */
  src: string;
  width: number;
  height: number;
  /** i18n key for the badge's alt text (shared with the get-app page). */
  altKey: string;
  biName: string;
}
interface AppCard {
  id: string;
  titleKey: string;
  /**
   * `comingSoon` is a PROMISE — reserve it for a surface with a real, tracked initiative behind it
   * (CarPlay: a filed Apple entitlement, `docs/car-surfaces-design.md`). `notAvailableYet` is the neutral
   * default for anything else: no commitment, no date, nothing implied. Mixing the two up is exactly the
   * bug this file shipped once — Apple Watch read "Coming soon" on desktop while iOS, Android and mobile
   * web all correctly said "Not available yet" for the same row, and the skill telling the assistant
   * never to imply a watch app is coming was then contradicted by the very UI it was describing.
   */
  state: 'available' | 'comingSoon' | 'notAvailableYet';
  stores: StoreLink[];
}

const APP_CARDS: AppCard[] = [
  {
    id: 'phone',
    titleKey: 'connectors.app.phone',
    state: 'available',
    stores: [
      { href: APP_STORE_URL, src: 'app-store-badge.svg', width: 90, height: 30, altKey: 'getApp.badgeAppStoreAlt', biName: 'open-app-store' },
      { href: GOOGLE_PLAY_URL, src: 'google-play-badge-trimmed.png', width: 105, height: 30, altKey: 'getApp.badgeGooglePlayAlt', biName: 'open-google-play' },
    ],
  },
  // Android Auto rides the phone app — a state, no separate store link.
  { id: 'androidauto', titleKey: 'connectors.app.androidAuto', state: 'available', stores: [] },
  // CarPlay pends Apple's entitlement — a state, not connectable.
  { id: 'carplay', titleKey: 'connectors.app.carplay', state: 'comingSoon', stores: [] },
];

/**
 * The hands-free surfaces (Figma parity with the Web & Apps cards, same plate, same states).
 *
 * Siri is `available` and the others are not, and the split is a fact about the platforms rather than a
 * roadmap: the three background-only App Intents ship today (`SymphonyAppIntents.swift`), so anything
 * Siri already speaks through — AirPods, any paired earbuds — reaches the agent with the app closed.
 * Apple documents no App-Intents-era path from a watch to an iPhone-only app, and Wear OS exposes only a
 * narrow exercise/media intent set, so neither is claimed as working — and `notAvailableYet`, not
 * `comingSoon`, is the honest state for it: there is no filed initiative behind a watch app the way there
 * is for CarPlay, so nothing should read as promised. Matches iOS/Android/mobile-web exactly.
 *
 * ⚠️ This section is about REACHING the agent. A wearable whose DATA the agent reads (a Plaud recorder's
 * transcripts, a ring's sleep) is an ordinary connector on the Tools tab — see `CHANNEL_SLUGS`.
 */
const WEARABLE_CARDS: AppCard[] = [
  { id: 'siri', titleKey: 'connectors.wearables.siri', state: 'available', stores: [] },
  { id: 'watch', titleKey: 'connectors.wearables.watch', state: 'notAvailableYet', stores: [] },
];

/** The Channels sections, in the redesign's order. Gemini is folded into `ai-coders`, so it is not a
 *  section of its own here (its route slug stays in `CHANNEL_SLUGS` — see the anchor below). */
const DISPLAY_SLUGS: ChannelSlug[] = ['whatsapp', 'meetings', 'ai-coders', 'symphony-apps', 'wearables'];

/** The surface cards a section renders, if it is one of the two card sections. */
const cardsFor = (slug: ChannelSlug): AppCard[] | null =>
  slug === 'symphony-apps' ? APP_CARDS : slug === 'wearables' ? WEARABLE_CARDS : null;

export function ChannelsBody({ target, query }: { target?: string; query: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const basePath = useBasePath();
  const productName = useProductName();
  const mention = `@${productName.toLowerCase()}`;
  const searching = query.trim().length > 0;

  /**
   * The text a section is matched on. AI coders matches on its item titles AND Gemini's (Gemini now
   * lives inside it); Symphony apps on its surface rows alongside the section title; WhatsApp on its
   * own title. Mirrors both native platforms.
   */
  const sectionSearchText = (slug: ChannelSlug): string => {
    if (slug === 'ai-coders') {
      return [...AI_CODERS_ITEM_TITLE_KEYS.map((key) => t(key)), t('connectors.channel.gemini-enterprise')].join(' ');
    }
    const cards = cardsFor(slug);
    if (cards) {
      return [t(`connectors.channel.${slug}`), ...cards.map((c) => t(c.titleKey))].join(' ');
    }
    return t(`connectors.channel.${slug}`);
  };

  // Meetings is behind `symphonyMeetingsAgent`. Dropped from the ORDER rather than skipped at render,
  // so search counts and the "no results" branch below see the same list the user can actually reach —
  // a hidden section that still matched a query would make the tab claim a result it never draws.
  const meetingsEnabled = useMeetingsAgentEnabled();
  const displaySlugs = meetingsEnabled ? DISPLAY_SLUGS : DISPLAY_SLUGS.filter((slug) => slug !== 'meetings');

  const visibleSections = filterChannelSections(displaySlugs, query, sectionSearchText);

  return (
    <div className={styles.sections}>
      {searching && visibleSections.length === 0 && (
        <div className={styles.noResults}>
          <Typography variant="h4">{t('search.noResults')}</Typography>
          <Typography variant="p2" color="secondary">
            {t('search.noResults.sub')}
          </Typography>
        </div>
      )}
      {visibleSections.map((slug) => {
        const cards = cardsFor(slug);
        return (
        <section
          key={slug}
          // Shared with the route builder, so a link can never point at an id the page doesn't render.
          id={connectorsTargetElementId('channels', slug)}
          className={styles.section}
          data-targeted={target === slug ? '' : undefined}
        >
          {/* WhatsApp and Meetings: the whole section — title, blurb and the cards — sits inside one
              sunken card (Figma 13936-256717). The remaining sections keep their title/blurb on the grey
              page.

              Meetings joins it because it is the same KIND of section: a channel with a connection and
              state of its own, rather than a row of links out. Left bare it read as a stray card on the
              page next to WhatsApp's framed block — the two are peers and have to be framed alike. */}
          {slug === 'whatsapp' || slug === 'meetings' ? (
            <Card variant="sunken" className={styles.sectionCard}>
              {slug === 'whatsapp' ? (
                <>
                  {/* Glyph-less on purpose: WhatsApp's mark is carried by each of its three cards
                      instead — see the note in `WhatsappChannelSection`. Its fuller blurb is shared
                      word-for-word with the mobile-web presentation. */}
                  <Typography variant="h4" className={styles.sectionTitle}>
                    {t(`connectors.channel.${slug}`)}
                  </Typography>
                  <Typography variant="p2" color="secondary" className={styles.about}>
                    {t('connectors.about.whatsapp.mobile', { productName, mention })}
                  </Typography>
                  <WhatsappChannelSection />
                </>
              ) : (
                /* Meetings draws its own title row, so its Connected badge and menu can share the
                   title's line. The title and the shared `connectors.about.*` line are still ours. */
                <MeetingsChannelSection
                  defaultExpanded={target === slug}
                  title={
                    <Typography variant="h4" className={styles.sectionTitle}>
                      {t(`connectors.channel.${slug}`)}
                    </Typography>
                  }
                  about={
                    <Typography variant="p2" color="secondary" className={styles.about}>
                      {t(`connectors.about.${slug}`)}
                    </Typography>
                  }
                />
              )}
            </Card>
          ) : (
            <>
              <Typography variant="h4" className={styles.sectionTitle}>
                {t(`connectors.channel.${slug}`)}
              </Typography>
              {/* What you can do here — the one line that makes the section legible on its own. */}
              <Typography variant="p2" color="secondary" className={styles.about}>
                {t(`connectors.about.${slug}`)}
              </Typography>

              {/* Web & Apps: Symphony's own surfaces as 1/3-pane cards (Figma 14013-148755). Left: title
                  (14/18 semibold) over its availability. Right (when it ships there): the official store
                  badges, each linking to that store. */}
              {cards ? (
                <div className={styles.appsGrid}>
                  {cards.map((card) => (
                    <Card key={card.id} variant="sunken" disablePadding className={styles.appCard}>
                      <span className={styles.appMeta}>
                        <span className={styles.appTitle}>{t(card.titleKey)}</span>
                        <span className={styles.appState}>{t(`connectors.state.${card.state}`)}</span>
                      </span>
                      {card.stores.length > 0 && (
                        <span className={styles.appActions}>
                          {card.stores.map((s) => (
                            <a
                              key={s.src}
                              className={styles.storeBadge}
                              href={s.href}
                              target="_blank"
                              rel="noreferrer"
                              onClick={() => logPageClickBi({ page_name: CHANNELS_ROUTE, button_name: s.biName })}
                            >
                              <img src={`${basePath}/${s.src}`} width={s.width} height={s.height} alt={t(s.altKey)} />
                            </a>
                          ))}
                        </span>
                      )}
                    </Card>
                  ))}
                </div>
              ) : null}

              {/* AI coders: the coder connect cards, Gemini Enterprise's card, and all agent connections. */}
              {slug === 'ai-coders' ? (
                <div className={styles.manage}>
                  {/* Three cards per row (Figma 14013-148717): Gemini, Claude Code, Claude, ChatGPT. */}
                  <div className={styles.aiGrid}>
                    <GeminiConnect />
                    <AiCodersConnect query={query} />
                  </div>
                  <AgentConnectionsList />
                  {/* Gemini's route slug stays in CHANNEL_SLUGS; its deeplink now scrolls to this section. */}
                  <span id={connectorsTargetElementId('channels', 'gemini-enterprise')} aria-hidden />
                </div>
              ) : null}
            </>
          )}
        </section>
        );
      })}
    </div>
  );
}
